import { useEffect, useState } from "react";

// How long to keep looking for the editor before giving up. It is found on the
// first try in practice; this only covers a composer that mounts late.
const ATTACH_RETRY_MS = 200;
const ATTACH_TIMEOUT_MS = 5000;

// Mutations arrive per keystroke and each check clones the editor, which is not
// free on a reply carrying a long quoted thread. Coalescing them costs nothing
// noticeable — this only drives whether a button looks enabled.
const CHECK_DEBOUNCE_MS = 150;

/**
 * Whether a composer currently holds anything worth refining (EAI-1006).
 *
 * For a contenteditable there is no React state to watch: the element is
 * written to directly, by the user typing but also by Otto, Use Template,
 * Insert Fields and audio transcription. Subscribing to any one of those misses
 * the others — Otto's generate, for instance, never dispatches setEmailBody, so
 * a redux subscription leaves the Refine button disabled over a composer that
 * visibly has a draft in it.
 *
 * Watching the element itself catches every writer, present and future, without
 * each one having to remember to announce itself.
 *
 * Composers backed by React state (a `<textarea>` whose onChange feeds
 * `messageText`) already re-render on change and don't need this.
 *
 * @param {object} adapter  see draftAdapters.js; needs `getElement` + `readDraft`
 * @returns {boolean}
 */
export default function useDraftPresence(adapter) {
  const [hasDraft, setHasDraft] = useState(false);

  useEffect(() => {
    if (!adapter?.getElement) return undefined;

    let observer;
    let retryTimer;
    let checkTimer;
    let waited = 0;

    const check = () => setHasDraft(!!(adapter.readDraft() || "").trim());
    const checkSoon = () => {
      clearTimeout(checkTimer);
      checkTimer = setTimeout(check, CHECK_DEBOUNCE_MS);
    };

    const attach = () => {
      const el = adapter.getElement();
      if (!el) {
        waited += ATTACH_RETRY_MS;
        if (waited < ATTACH_TIMEOUT_MS) retryTimer = setTimeout(attach, ATTACH_RETRY_MS);
        return;
      }
      check();
      // characterData covers typing inside an existing node; childList/subtree
      // cover wholesale replacement, which is how the programmatic writers work.
      observer = new MutationObserver(checkSoon);
      observer.observe(el, { childList: true, subtree: true, characterData: true });
    };

    attach();

    return () => {
      clearTimeout(retryTimer);
      clearTimeout(checkTimer);
      observer?.disconnect();
    };
  }, [adapter]);

  return hasDraft;
}
